import { clsx } from 'clsx';
import { useReducedMotion } from 'framer-motion';
import { forwardRef, type AriaAttributes, type ReactNode } from 'react';

import * as styles from './AiLoadingIndicator.sty.js';
import { AiLoadingIndicatorIcon } from './AiLoadingIndicatorIcon.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useId } from '../../core/hooks/useId.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import { useSlots } from '../../core/hooks/useSlots.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { DOMProps } from '../../core/types/dom.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import { Flex } from '../../layouts/flex/Flex.js';

const COMPONENT_NAME = 'AiLoadingIndicator';

/**
 * Accepted properties for `AiLoadingIndicator`.
 * @public
 */
export interface AiLoadingIndicatorProps
  extends DOMProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    AriaLabelingProps,
    BehaviorTrackingProps {
  // DEPRECATION: [APPDEV-16782] BREAKING-CHANGE: Remove the `aria-disabled` prop.
  /**
   *  Whether the element and all focusable descendants are disabled.
   * `aria-disabled="true"` only semantically exposes these elements as being disabled.
   *
   * @deprecated Will be removed. Please remove this prop from your implementation, since the attribute is only intended for interactive elements.
   */
  'aria-disabled'?: AriaAttributes['aria-disabled'];
  /**
   * The label shown in the indicator.
   */
  children: ReactNode;
  /**
   * The `aria-valuetext` attribute provides a human-readable text alternative for assistive technologies about the loading progress.
   * @remarks Because `AiLoadingIndicator` is indeterminate, `aria-valuetext` is used instead of `aria-valuenow` to describe the progress.
   * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-valuetext
   */
  'aria-valuetext'?: string;
}

/**
 * Use the `AiLoadingIndicator` component to indicate that AI-generated content is being loaded.
 */
const InternalAiLoadingIndicator = forwardRef<
  HTMLDivElement,
  AiLoadingIndicatorProps
>((props, forwardedRef) => {
  const {
    id,
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'aria-valuetext': ariaValuetext,
    ...remainingProps
  } = props;

  const [slots, label] = useSlots(children, {
    icon: AiLoadingIndicatorIcon,
  });

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);
  const { 'aria-labelledby': ariaLabelledBy, ...remainingAriaLabelingProps } =
    ariaLabelingProps;

  const labelId = useId();
  const shouldReduceMotion = useReducedMotion();

  return (
    <Flex
      id={id}
      ref={forwardedRef}
      role="progressbar"
      alignItems="flex-start"
      gap={8}
      className={clsx(styles.container, consumerClassName)}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      aria-labelledby={ariaLabelledBy ?? labelId}
      aria-valuetext={ariaValuetext}
      {...remainingAriaLabelingProps}
      {...behavioralTrackingProps}
      {...forwardProps}
      data-dt-component={COMPONENT_NAME}
    >
      {slots.icon}
      <div
        id={labelId}
        className={clsx(
          styles.label,
          !shouldReduceMotion && styles.labelAnimation,
        )}
      >
        {label}
      </div>
    </Flex>
  );
});

(
  InternalAiLoadingIndicator as typeof InternalAiLoadingIndicator & {
    displayName: string;
  }
).displayName = COMPONENT_NAME;

/**
 * Use the `AiLoadingIndicator` component to indicate that AI-generated content is being loaded.
 * @public
 */
export const AiLoadingIndicator = Object.assign(InternalAiLoadingIndicator, {
  Icon: AiLoadingIndicatorIcon,
});
